import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import * as L from "lucide-react";
import { WindDownRecipeCard, DeliveryToggle, TestEmailButton } from "./integrations";
import PushDeliveryRow from "@/components/PushDeliveryRow";

/**
 * RitualsScreen — daily rhythms Rammy runs for you.
 *
 * These used to live inside the Connections page which was a mismatch:
 * connections are *sources of context*, whereas rituals are *scheduled
 * actions Rammy takes for you*. Splitting them made both pages breathe.
 *
 * Hosts (v1):
 *   • Morning briefing — warm calendar+weather recap at a chosen time.
 *   • Evening wind-down — tomorrow-at-a-glance + a journal prompt.
 */
export function RitualsScreen({ user, go }) {
  const [recipe, setRecipe] = useState({
    enabled: false,
    time_local: "07:30",
    delivery: { in_app: true, email: false, sms: false, telegram: false, push: false, email_addr: "", phone_num: "" },
  });
  const [running, setRunning] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/list");
      const rmb = data.recipes?.morning_briefing || {};
      setRecipe({
        enabled: !!rmb.enabled,
        time_local: rmb.time_local || "07:30",
        delivery: {
          in_app: rmb.delivery?.in_app ?? true,
          email: !!rmb.delivery?.email,
          sms: !!rmb.delivery?.sms,
          telegram: !!rmb.delivery?.telegram,
          push: !!rmb.delivery?.push,
          email_addr: rmb.delivery?.email_addr || user?.email || "",
          phone_num: rmb.delivery?.phone_num || "",
        },
      });
    } catch {
      toast.error("Couldn't load your rituals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runBriefing = async () => {
    setRunning(true);
    setBriefing(null);
    try {
      const { data } = await api.post("/integrations/briefing/run");
      setBriefing(data);
    } catch {
      toast.error("Briefing didn't run — try again");
    } finally {
      setRunning(false);
    }
  };

  const saveRecipe = async (patch) => {
    const next = { ...recipe, ...patch, delivery: { ...recipe.delivery, ...(patch.delivery || {}) } };
    setRecipe(next);
    try {
      await api.patch("/integrations/recipes/morning-briefing", {
        enabled: next.enabled,
        time_local: next.time_local,
        delivery: next.delivery,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
      load();
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="rituals-screen">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">Daily rhythms</div>
        <h1 className="text-2xl md:text-3xl font-display mt-1">Rituals with Rammy.</h1>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1 leading-relaxed max-w-xl">
          Small, warm moments the day is anchored on. Rammy runs them on schedule and drops the result where you want it.
        </p>
      </header>

      {loading && <div className="py-8 text-sm text-[var(--rammy-text-muted)]">Loading your rhythms…</div>}

      {!loading && (
        <>
          {/* Morning briefing — the marquee card */}
          <section
            className="rounded-3xl bg-[var(--rm-ink-900)] text-white p-6 md:p-7 rammy-shadow"
            data-testid="ritual-morning-briefing"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <span className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                  <L.Sunrise size={22} className="text-[var(--rammy-cyan)]"/>
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Sunrise ritual</div>
                  <div className="mt-0.5 font-display text-xl md:text-2xl">Morning briefing</div>
                  <p className="text-sm text-white/70 mt-1 max-w-md leading-relaxed">
                    A human recap of your calendar, weather and what to hold gently for today. No lists — a paragraph.
                  </p>
                </div>
              </div>
              <Switch
                checked={!!recipe.enabled}
                onCheckedChange={(v) => saveRecipe({ enabled: v })}
                data-testid="ritual-morning-enabled"
              />
            </div>

            {/* Time + run row */}
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2 text-xs">
                <L.Clock size={12}/>
                <input
                  type="time"
                  value={recipe.time_local}
                  onChange={(e) => setRecipe((r) => ({ ...r, time_local: e.target.value }))}
                  onBlur={(e) => saveRecipe({ time_local: e.target.value })}
                  className="bg-transparent outline-none w-[70px] text-white"
                  data-testid="ritual-morning-time"
                />
                <span className="text-white/50">local</span>
              </div>
              <Button
                onClick={runBriefing}
                disabled={running}
                className="btn-pill h-9 px-4 bg-[var(--rammy-cyan)] text-[var(--rammy-text)] hover:bg-[var(--rammy-cyan)]/90"
                data-testid="ritual-morning-run"
              >
                {running ? (
                  <><L.Loader2 size={14} className="animate-spin mr-1.5"/> Running…</>
                ) : (
                  <><L.Zap size={14} className="mr-1.5"/> Run briefing now</>
                )}
              </Button>
            </div>

            {/* Delivery preferences */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-3">Send my briefing to</div>
              <div className="space-y-3">
                <DeliveryToggle
                  label="In-app inbox"
                  hint="Delivered inside Rammy — no notifications, always private."
                  checked={!!recipe.delivery?.in_app}
                  onChange={(v) => saveRecipe({ delivery: { in_app: v } })}
                  testid="ritual-delivery-in-app"
                />
                <DeliveryToggle
                  label="Email"
                  hint="Land the briefing in your inbox each morning."
                  checked={!!recipe.delivery?.email}
                  onChange={(v) => saveRecipe({ delivery: { email: v } })}
                  testid="ritual-delivery-email"
                  extra={recipe.delivery?.email && (
                    <>
                      <input
                        type="email"
                        value={recipe.delivery?.email_addr || ""}
                        onChange={(e) => setRecipe((r) => ({ ...r, delivery: { ...r.delivery, email_addr: e.target.value } }))}
                        onBlur={(e) => saveRecipe({ delivery: { email_addr: e.target.value } })}
                        placeholder="you@work.com"
                        className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-white/30 outline-none focus:border-[var(--rammy-cyan)]"
                        data-testid="ritual-delivery-email-addr"
                      />
                      <TestEmailButton addr={recipe.delivery?.email_addr}/>
                    </>
                  )}
                  badge={<span className="text-[9px] uppercase tracking-widest bg-white/10 text-white/60 px-1.5 py-0.5 rounded-full">Beta</span>}
                />
                <DeliveryToggle
                  label="Telegram"
                  hint="Land the briefing in your Rammy Telegram thread."
                  checked={!!recipe.delivery?.telegram}
                  onChange={(v) => saveRecipe({ delivery: { telegram: v } })}
                  testid="ritual-delivery-telegram"
                  badge={<span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan)] px-1.5 py-0.5 rounded-full">Live</span>}
                />
                <PushDeliveryRow
                  checked={!!recipe.delivery?.push}
                  onChange={(v) => saveRecipe({ delivery: { push: v } })}
                  testid="ritual-delivery-push"
                />
                <DeliveryToggle
                  label="SMS"
                  hint="Text delivery for phone-first mornings."
                  checked={!!recipe.delivery?.sms}
                  onChange={(v) => saveRecipe({ delivery: { sms: v } })}
                  testid="ritual-delivery-sms"
                  disabled
                  badge={<span className="text-[9px] uppercase tracking-widest bg-white/10 text-white/60 px-1.5 py-0.5 rounded-full">Plus soon</span>}
                />
              </div>
              <div className="mt-3 text-[11px] text-white/40 leading-relaxed">
                In-app is always on. Email, Telegram &amp; Push are live — hit &ldquo;Send a test&rdquo; on each to confirm. Pair Telegram from Connections first. For iPhone push, install Rammy to your home screen.
              </div>
            </div>

            {briefing && (
              <div
                className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-4 text-sm leading-relaxed whitespace-pre-wrap fade-up"
                data-testid="ritual-briefing-output"
              >
                {briefing.brief}
                <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40">
                  {briefing.event_count} event{briefing.event_count === 1 ? "" : "s"} · weather {briefing.has_weather ? "on" : "off"}
                </div>
              </div>
            )}
          </section>

          {/* Evening wind-down — a light card, quieter energy */}
          <WindDownRecipeCard onNav={() => go("settings")}/>

          {/* Future rituals teaser */}
          <section className="rounded-3xl border border-dashed border-[var(--rammy-border)] p-5 md:p-6" data-testid="ritual-future">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">
              <L.Sparkles size={12}/> Coming rituals
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm text-[var(--rammy-text-sec)]">
              <FutureRitual icon={<L.PenLine size={14}/>} title="Sunday reset" hint="A weekly reflection & the week ahead."/>
              <FutureRitual icon={<L.HeartHandshake size={14}/>} title="Gratitude ping" hint="One tiny thanks-note, thrice a week."/>
              <FutureRitual icon={<L.Moon size={14}/>} title="Sleep story" hint="A five-minute bedtime story, in your voice."/>
              <FutureRitual icon={<L.Timer size={14}/>} title="Pomodoro helper" hint="Timed focus with a warm pep-talk."/>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function FutureRitual({ icon, title, hint }) {
  return (
    <div className="flex items-start gap-2.5 rounded-2xl bg-[var(--rammy-surface-alt)]/60 p-3">
      <span className="w-7 h-7 rounded-lg bg-[var(--rm-surface)] flex items-center justify-center text-[var(--rammy-cyan-dark)] shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-xs text-[var(--rammy-text-muted)] leading-relaxed">{hint}</div>
      </div>
    </div>
  );
}
